import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import { FadeIn } from '@/components/marketing/FadeIn';

const About: React.FC = () => {
  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      <Header />

      <FadeIn as="section" className="max-w-3xl mx-auto px-6 py-20">
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.1]">
          Built by people who felt it too.
        </h1>
      </FadeIn>

      <section className="border-t border-border/60">
        <div className="max-w-3xl mx-auto px-6 py-20 space-y-6">
          <FadeIn>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
              The idea came from a conversation.
            </h2>
          </FadeIn>
          <FadeIn delay={100}>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              Dan Ariely and Julianne Wurm have been friends for 15 years. Like a lot of people,
              they found themselves talking about what social media had become — the dogpiling,
              the heartless comments, the ill-informed pile-ons that seemed to materialize out of
              nowhere and leave real damage behind.
            </p>
          </FadeIn>
          <FadeIn delay={150}>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              Julianne said it out loud: wouldn't it be something to have a fence you could put
              around that. Not to make it disappear — people can say what they want. But it
              doesn't have to reach you. It can exist on the other side of a line you drew
              yourself.
            </p>
          </FadeIn>
          <FadeIn delay={200}>
            <blockquote
              className="border-l-2 border-foreground/20 pl-6 italic text-foreground my-8"
              style={{ transitionDuration: '700ms' }}
            >
              They can exist. They just don't have to reach me.
            </blockquote>
          </FadeIn>
          <FadeIn delay={150}>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              The more they talked, the more they wanted to build it.
            </p>
          </FadeIn>
          <FadeIn delay={200}>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              What they realized, slowly, was that they'd both been studying this problem for
              decades — just in different fields. Dan had spent his career documenting how systems
              exploit human psychology, how the architecture of a choice environment determines
              what people feel and decide. Julianne had spent hers studying how physical
              environments shape children's learning and experience — the Reggio Emilia principle
              that where you are changes who you become. Social media is an environment. And like
              any environment, it can be designed well or designed badly. They decided to design
              it better.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="border-t border-border/60 bg-muted/30">
        <div className="max-w-3xl mx-auto px-6 py-20 space-y-8">
          <FadeIn>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
              Decades of expertise. One new problem.
            </h2>
          </FadeIn>

          <FadeIn delay={100}>
            <article>
              <h3 className="text-lg font-semibold">Dan Ariely</h3>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mt-1">
                Co-founder
              </p>
              <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                Dan is the James B. Duke Professor of Psychology and Behavioral Economics at Duke
                University, and the author of <em>Predictably Irrational</em>, <em>The Upside of
                Irrationality</em>, and <em>The Honest Truth About Dishonesty</em>. He has spent
                30 years studying how environments shape behavior, how systems exploit attention,
                and why irrational decisions feel completely rational in the moment. Social media
                is the most documented example of these forces in history. Dan brought that lens
                to SocialScoop — and the product is better for it.
              </p>
            </article>
          </FadeIn>

          <FadeIn delay={150}>
            <article>
              <h3 className="text-lg font-semibold">Julianne Wurm, EdD</h3>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mt-1">
                Co-founder &amp; Builder
              </p>
              <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                Julianne is a best-selling author whose books <em>Working in the Reggio Way</em>{' '}
                and <em>More Working in the Reggio Way</em> brought the Reggio Emilia educational
                philosophy to practitioners across the country. The Reggio approach rests on a
                foundational idea: environment shapes experience — that the spaces we inhabit
                determine what we feel, learn, and become. After spending years immersed in AI and
                teaching herself to build with it, Julianne brought that same conviction to a new
                environment: the social media feed. She built SocialScoop.
              </p>
            </article>
          </FadeIn>

          <FadeIn delay={200}>
            <p className="text-base text-muted-foreground leading-relaxed">
              Together they are leveraging decades of expertise — in human psychology, in
              behavioral design, in how environments shape experience — to solve a problem that
              didn't exist thirty years ago and now touches billions of people every day.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="border-t border-border/60">
        <div className="max-w-4xl mx-auto px-6 py-20">
          <FadeIn>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-10">
              A few things we believe.
            </h2>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <FadeIn delay={100}>
              <article className="rounded-xl border border-border bg-card p-7 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 h-full">
                <h3 className="text-base font-semibold">Your attention is yours.</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  Platforms are designed to maximize time on screen. We're designed to maximize
                  signal per minute. Those are different goals.
                </p>
              </article>
            </FadeIn>
            <FadeIn delay={200}>
              <article className="rounded-xl border border-border bg-card p-7 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 h-full">
                <h3 className="text-base font-semibold">Transparency over dark patterns.</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  We charge for Reddit because Reddit charges us. We'll always tell you why
                  something costs what it costs. Your data is never the product.
                </p>
              </article>
            </FadeIn>
            <FadeIn delay={300}>
              <article className="rounded-xl border border-border bg-card p-7 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 h-full">
                <h3 className="text-base font-semibold">Filtered is not censored.</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  Every piece of content you fence goes to the Reviewed Queue — stored, not
                  deleted. You're in control of what you see, not what exists.
                </p>
              </article>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="border-t border-border/60 bg-muted/30">
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <FadeIn>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">
              We're just getting started.
            </h2>
          </FadeIn>
          <FadeIn delay={100}>
            <p className="mt-6 text-base text-muted-foreground leading-relaxed">
              SocialScoop is in private beta. We're looking for 500 founding members who already
              know they want a quieter, more intentional feed.
            </p>
          </FadeIn>
          <FadeIn delay={200}>
            <div className="mt-8">
              <Button asChild size="lg">
                <Link to="/auth">Join the Founding Group</Link>
              </Button>
            </div>
          </FadeIn>
        </div>
      </section>

      <footer className="border-t border-border/60">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-muted-foreground">
          <div>© 2026 SocialScoop.ai — Built by humans, for humans.</div>
          <nav className="flex flex-wrap gap-5">
            <Link to="/features" className="hover:text-foreground transition-colors">Features</Link>
            <Link to="/how-it-works" className="hover:text-foreground transition-colors">How It Works</Link>
            <Link to="/about" className="hover:text-foreground transition-colors">About</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link to="/data-deletion" className="hover:text-foreground transition-colors">Data Deletion</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default About;
